// Shared classifier for POST /userCase/saveUserCase responses.
//
// The endpoint returns business outcomes as HTTP 200 with the meaningful code
// in `body.statusCode` — callers MUST branch on that, not the HTTP status.
// This helper collapses the status matrix from the contract into a single
// normalized result so every caller (Initial Assessment "I", Treatment Plan
// "T", Chemo Sitting "C") handles success / warnings / blocks / errors the
// same way.
//
// See docs/save-usercase-api-contract.md (§Status matrix, §Quick reference).

export const SaveOutcome = {
  SUCCESS: "success", // 200 — saved (possibly with warnings)
  BLOCKED: "blocked", // 451 / 403 — not saved; reason in `message`
  ERROR: "error", // 400 / unexpected / unparseable — generic failure
};

// Normalizes a parsed saveUserCase response body into:
//   { outcome, saved, warnings, message, statusCode }
// - saved:    parsed DTO from `message` on success (null if absent/unparseable)
// - warnings: non-empty array of warning strings on a 200 "saved with
//             warnings" (else null) — the save DID succeed
// - message:  human-readable reason for BLOCKED / ERROR (else null)
export function classifySaveUserCaseResponse(res) {
  const statusCode = res && res.statusCode;

  if (statusCode === 200) {
    let saved = null;
    if (res.message) {
      try {
        saved = JSON.parse(res.message);
      } catch (e) {
        // 200 success payload should always be a JSON string; if it is not,
        // treat it as a successful-but-empty save rather than a hard failure.
        saved = null;
      }
    }

    // Per contract `subErrors` is an array of strings when present; be lenient
    // in case a single string slips through.
    let warnings = null;
    if (Array.isArray(res.subErrors)) {
      warnings = res.subErrors.length > 0 ? res.subErrors : null;
    } else if (res.subErrors) {
      warnings = [String(res.subErrors)];
    }

    return { outcome: SaveOutcome.SUCCESS, saved, warnings, message: null, statusCode };
  }

  if (statusCode === 451 || statusCode === 403) {
    return {
      outcome: SaveOutcome.BLOCKED,
      saved: null,
      warnings: null,
      message:
        (res && res.message) || "Save was blocked. Please review and try again.",
      statusCode,
    };
  }

  // 400 / unexpected / missing body
  return {
    outcome: SaveOutcome.ERROR,
    saved: null,
    warnings: null,
    message: "Something went wrong while saving. Please retry.",
    statusCode,
  };
}
